import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { currency } from "@/lib/format";
import type { Booking, BookingDeposit } from "@/types/domain";

// Security deposit: held during the stay, outside the bill (no GST, not on the invoice, not a payment). When it is
// settled, part of it can pay the bill and the rest goes back. The final check-out waits until it is settled.

const MODES = [["CASH", "Cash"], ["UPI", "UPI"], ["CARD", "Card"], ["BANK_TRANSFER", "Bank transfer"], ["OTHER", "Other"]] as const;
const modeLabel = (mode?: string | null) => MODES.find(([value]) => value === mode)?.[1] ?? mode ?? "";

export function DepositPanel({ booking, due, canTake, canSettle, onUpdated }: {
  booking: Booking;
  /** Bill balance still due (negative = refund due). */
  due: number;
  canTake: boolean;
  canSettle: boolean;
  onUpdated: (booking: Booking) => void;
}) {
  const deposits = booking.deposits ?? [];
  const held = deposits.find((deposit) => deposit.status === "HELD");
  const live = booking.bookingStatus === "CONFIRMED" || booking.bookingStatus === "CHECKED_IN";
  const [mode, setMode] = useState<"take" | "settle" | null>(null);
  if (!deposits.length && !(live && canTake)) return null;

  return (
    <section className="booking-line-section deposit-panel">
      <div className="booking-line-section__head">
        <h4><ShieldCheck size={16} /> Security deposit</h4>
        {!held && live && canTake && mode !== "take" && <button type="button" className="booking-line-section__add" onClick={() => setMode("take")}>Take deposit</button>}
        {held && canSettle && mode !== "settle" && <button type="button" className="booking-line-section__add" onClick={() => setMode("settle")}>Settle / return</button>}
      </div>
      {deposits.map((deposit) => <DepositRow key={deposit.id} deposit={deposit} />)}
      {!deposits.length && mode !== "take" && <p className="booking-line-section__empty">No deposit taken. It is kept separate from the bill.</p>}
      {held && <p className="invoice-panel__hint">Settle it before the final check-out - part of it can pay the bill.</p>}
      {mode === "take" && <TakeForm booking={booking} onCancel={() => setMode(null)} onDone={(updated) => { setMode(null); onUpdated(updated); }} />}
      {mode === "settle" && held && <SettleForm booking={booking} deposit={held} due={due} onCancel={() => setMode(null)} onDone={(updated) => { setMode(null); onUpdated(updated); }} />}
    </section>
  );
}

function DepositRow({ deposit }: { deposit: BookingDeposit }) {
  const amount = Number(deposit.amount);
  return (
    <div className="invoice-panel__note">
      <div>
        <strong>{currency.format(amount)} · {modeLabel(deposit.paymentMode)} · {deposit.status === "HELD" ? "held" : "settled"}</strong>
        <small>
          Taken {new Date(deposit.takenAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
          {deposit.status === "SETTLED" && ` · ${Number(deposit.usedForBill) > 0 ? `${currency.format(Number(deposit.usedForBill))} used for the bill · ` : ""}${currency.format(Number(deposit.returnedAmount))} returned${deposit.returnMode ? ` (${modeLabel(deposit.returnMode)})` : ""}${deposit.note ? ` · ${deposit.note}` : ""}`}
        </small>
      </div>
    </div>
  );
}

function TakeForm({ booking, onCancel, onDone }: { booking: Booking; onCancel: () => void; onDone: (booking: Booking) => void }) {
  const suggested = Number(booking.stayProfile?.defaultDeposit ?? 0);
  const [amount, setAmount] = useState(suggested > 0 ? String(suggested) : "");
  const [paymentMode, setPaymentMode] = useState("CASH");
  const [reference, setReference] = useState("");
  const take = useMutation({
    mutationFn: () => api<Booking>(`/bookings/${booking.id}/deposit`, { method: "POST", body: { amount: Number(amount), paymentMode, transactionRef: reference.trim() || undefined } }),
    onSuccess: onDone,
  });
  return (
    <div className="booking-inline-form">
      <label>Amount<input type="number" min="0" step="1" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
      <label>Mode<select value={paymentMode} onChange={(event) => setPaymentMode(event.target.value)}>{MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      {paymentMode !== "CASH" && <label>Reference<input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="UPI / card reference" /></label>}
      {take.error && <div className="form-error">{take.error.message}</div>}
      <div className="bd-actions__row">
        <Button variant="secondary" disabled={take.isPending} onClick={onCancel}>Cancel</Button>
        <Button loading={take.isPending} disabled={!(Number(amount) > 0)} onClick={() => take.mutate()}>Take {Number(amount) > 0 ? currency.format(Number(amount)) : "deposit"}</Button>
      </div>
    </div>
  );
}

function SettleForm({ booking, deposit, due, onCancel, onDone }: { booking: Booking; deposit: BookingDeposit; due: number; onCancel: () => void; onDone: (booking: Booking) => void }) {
  const amount = Number(deposit.amount);
  const maxUse = Math.max(0, Math.min(amount, due));
  const [useForBill, setUseForBill] = useState(String(Math.round(maxUse * 100) / 100));
  const [returnMode, setReturnMode] = useState(deposit.paymentMode);
  const [note, setNote] = useState("");
  const used = Number(useForBill) || 0;
  const returned = Math.round((amount - used) * 100) / 100;
  const settle = useMutation({
    mutationFn: () => api<Booking>(`/bookings/${booking.id}/deposit/settle`, { method: "POST", body: { useForBill: used, returnMode, note: note.trim() || undefined } }),
    onSuccess: onDone,
  });
  const valid = used >= 0 && used <= maxUse + 0.005;
  return (
    <div className="booking-inline-form">
      <p className="booking-inline-form__hint">Held {currency.format(amount)}. {due > 0 ? `${currency.format(due)} is due on the bill.` : "Nothing is due on the bill."}</p>
      <label>Use for the bill<input type="number" min="0" max={maxUse} step="0.01" value={useForBill} onChange={(event) => setUseForBill(event.target.value)} /></label>
      {!valid && <div className="form-error">At most {currency.format(maxUse)} can pay the bill.</div>}
      {returned > 0 && <label>Return {currency.format(returned)} by<select value={returnMode} onChange={(event) => setReturnMode(event.target.value as BookingDeposit["paymentMode"])}>{MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>}
      <label>Note<input value={note} onChange={(event) => setNote(event.target.value)} placeholder="e.g. no damage / towel missing" /></label>
      {settle.error && <div className="form-error">{settle.error.message}</div>}
      <div className="bd-actions__row">
        <Button variant="secondary" disabled={settle.isPending} onClick={onCancel}>Cancel</Button>
        <Button loading={settle.isPending} disabled={!valid} onClick={() => settle.mutate()}>{returned > 0 ? `Return ${currency.format(returned)}` : "Use all for the bill"}</Button>
      </div>
    </div>
  );
}
